<section class="self-service-chat-shell">
  <aside class="chat-sidebar">
    <div class="sidebar-brand">
      <div class="brand-icon">
        <mat-icon>forum</mat-icon>
      </div>
      <div>
        <h2>Chat</h2>
        <small>Self-service assist</small>
      </div>
    </div>

    <div class="sidebar-section">
      <p class="sidebar-label">Conversations</p>
      <button type="button" class="new-chat-button" (click)="startNewChat()">
        <mat-icon>add</mat-icon>
        New Chat
      </button>
    </div>

    <div class="conversation-list">
      <button
        type="button"
        class="conversation-card active"
        *ngFor="let conversation of conversations"
      >
        <div class="conversation-card-top">
          <strong>{{ conversation.title }}</strong>
          <span>{{ conversation.updatedAt }}</span>
        </div>
        <small>{{ conversation.subtitle }}</small>
      </button>
    </div>

    <div class="sidebar-footer">
      <p>Need help?</p>
      <small>Upload a file and map the missing fields using your entity columns.</small>
    </div>
  </aside>

  <div class="chat-main">
    <header class="chat-main-header">
      <div>
        <h3>{{ activeConversation.title }}</h3>
        <div class="status-pill">
          <span class="status-dot"></span>
          {{ isLoading ? 'Analyzing' : 'In Progress' }}
        </div>
      </div>
      <button type="button" class="close-chat-button" (click)="closeChat()" matTooltip="Close chat">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="chat-scroll-area">
      <div class="message-stack">
        <div
          *ngFor="let message of messages"
          class="message-row"
          [class.message-row-user]="message.sender === 'user'"
        >
          <div class="agent-avatar" *ngIf="message.sender === 'agent'">
            <mat-icon>smart_toy</mat-icon>
          </div>

          <div class="message-bubble" [class.user-message]="message.sender === 'user'">
            <div class="message-text" *ngIf="message.sender === 'agent'">
              <app-streaming-text [content]="message.text"></app-streaming-text>
            </div>
            <div class="message-text" *ngIf="message.sender === 'user'">
              {{ message.text }}
            </div>

            <div *ngIf="message.files?.length" class="message-files">
              <div *ngFor="let file of message.files" class="message-file-chip">
                <img *ngIf="isImageFile(file)" [src]="getFilePreviewUrl(file)" [alt]="file.name" />
                <mat-icon *ngIf="!isImageFile(file)">attach_file</mat-icon>
                <span>{{ file.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="isLoading" class="message-row">
          <div class="agent-avatar">
            <mat-icon>smart_toy</mat-icon>
          </div>
          <div class="message-bubble typing-indicator" aria-live="polite">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div class="dynamic-form-card" *ngIf="missingFields.length">
          <div class="dynamic-form-header">
            <div>
              <h4>Please map the missing fields</h4>
              <p>
                Dropdown options come from the API response when available, otherwise from the currently loaded entity or dataset columns.
              </p>
            </div>
          </div>

          <div class="dynamic-form-grid">
            <div class="field-block" *ngFor="let field of missingFields">
              <label [for]="'chat-field-' + field.key">
                {{ field.label }}
                <span class="field-source">{{ field.source === 'response' ? 'API options' : 'Column options' }}</span>
              </label>
              <ng-select
                [labelForId]="'chat-field-' + field.key"
                [items]="field.options"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [searchable]="true"
                [placeholder]="'Select a column for ' + field.label"
                [ngModel]="fieldSelections[field.key]"
                (ngModelChange)="onMissingFieldSelectionChange(field.key, $event)"
              ></ng-select>
            </div>
          </div>
        </div>

        <div class="template-editor-card" *ngIf="hasTemplateEditor">
          <div class="template-editor-header">
            <div>
              <h4>Edit Generated DVM JSON</h4>
              <p>
                Map the response keys from the PPT analysis to entity columns, edit the final backend key when needed,
                and keep the live JSON preview in sync.
              </p>
            </div>

            <div class="template-editor-metrics">
              <div class="metric-chip">
                <span>Keys tracked</span>
                <strong>{{ templateKeyMappings.length }}</strong>
              </div>
              <div class="metric-chip">
                <span>Changed</span>
                <strong>{{ templateChangedCount }}</strong>
              </div>
              <div class="metric-chip">
                <span>Usages</span>
                <strong>{{ templateUsageCount }}</strong>
              </div>
              <div class="metric-chip metric-chip--good">
                <span>Mapped</span>
                <strong>{{ mappedTemplateKeyCount }}</strong>
              </div>
              <div class="metric-chip metric-chip--warn">
                <span>Need review</span>
                <strong>{{ unresolvedTemplateKeyCount }}</strong>
              </div>
            </div>
          </div>

          <div class="template-editor-layout">
            <section class="template-editor-panel">
              <div class="template-editor-toolbar">
                <div class="editor-field">
                  <label for="template-entity-select">Select entity</label>
                  <ng-select
                    [labelForId]="'template-entity-select'"
                    [items]="entityOptions"
                    [searchable]="true"
                    [clearable]="true"
                    [loading]="isEntityOptionsLoading"
                    [placeholder]="isEntityOptionsLoading ? 'Loading entities...' : 'Choose an entity'"
                    [ngModel]="selectedTemplateEntity"
                    (ngModelChange)="onTemplateEntitySelectionChange($event)"
                  ></ng-select>
                  <small>Loads entity columns from <code>/spriced/platform/getAllEntities/NontimeSeriesEntities</code> and the selected entity table.</small>
                </div>

                <div class="editor-field">
                  <label for="template-entity-name">Entity name</label>
                  <input
                    id="template-entity-name"
                    type="text"
                    [ngModel]="templateEntityName"
                    (ngModelChange)="onTemplateEntityNameInput($event)"
                    placeholder="Enter entity name"
                  />
                  <small>
                    Updates all matching API entity references such as
                    <code>/spriced/platform/&lt;entity_name&gt;/dynamic_filter</code>.
                  </small>
                </div>

                <div class="editor-field">
                  <label for="template-search">Search keys or usage paths</label>
                  <input
                    id="template-search"
                    type="text"
                    [(ngModel)]="templateSearch"
                    placeholder="Search label, response key, final key, or usage path..."
                  />
                </div>
              </div>

              <div class="mapping-helper-bar">
                <div class="mapping-helper-copy">
                  <strong>Mapping flow</strong>
                  <span>Select an entity, review the response keys, then map each one to a column or edit the final backend key directly.</span>
                </div>
                <button
                  type="button"
                  class="secondary-action compact-action"
                  [disabled]="!entityColumns.length"
                  (click)="autoMatchTemplateKeys()"
                >
                  Auto match columns
                </button>
              </div>

              <div class="entity-columns-card" *ngIf="selectedTemplateEntity || entityColumns.length || entityLoadError">
                <div class="entity-columns-header">
                  <div>
                    <h5>Entity columns</h5>
                    <small *ngIf="selectedTemplateEntity">Columns for <strong>{{ selectedTemplateEntity }}</strong></small>
                  </div>
                  <span class="usage-count" *ngIf="entityColumns.length">{{ entityColumns.length }} columns</span>
                </div>

                <div class="entity-columns-loading" *ngIf="isEntityColumnsLoading">Loading entity columns...</div>
                <div class="chat-error entity-columns-error" *ngIf="entityLoadError">{{ entityLoadError }}</div>

                <div class="entity-column-chip-list" *ngIf="entityColumns.length">
                  <code *ngFor="let column of entityColumns">{{ column }}</code>
                </div>
              </div>

              <div class="mapping-table-shell">
                <div class="mapping-table-head">
                  <span>Response key</span>
                  <span>Entity column mapping</span>
                  <span>New key</span>
                  <span>Display label</span>
                  <span>Usage / status</span>
                </div>

                <div class="mapping-row" *ngFor="let mapping of filteredTemplateKeyMappings; trackBy: trackTemplateKeyMapping">
                  <div class="mapping-cell mapping-cell--current">
                    <div class="mapping-current-header">
                      <span class="mapping-status-badge" [class.is-mapped]="isTemplateKeyMappedToEntityColumn(mapping)" [class.needs-attention]="templateKeyNeedsAttention(mapping)">
                        {{ getTemplateKeyStatusLabel(mapping) }}
                      </span>
                      <strong>{{ mapping.displayLabel }}</strong>
                    </div>
                    <code>{{ mapping.originalKey }}</code>
                  </div>

                  <div class="mapping-cell mapping-cell--select">
                    <ng-select
                      [items]="entityColumns"
                      [searchable]="true"
                      [clearable]="true"
                      [placeholder]="entityColumns.length ? 'Map to entity column' : 'Select entity first'"
                      [ngModel]="entityColumns.includes(mapping.nextKey.trim()) ? mapping.nextKey.trim() : null"
                      (ngModelChange)="onTemplateEntityColumnSelect(mapping, $event)"
                    ></ng-select>
                    <div class="mapping-suggestions" *ngIf="getTemplateKeySuggestions(mapping).length">
                      <button
                        type="button"
                        class="suggestion-chip"
                        *ngFor="let suggestion of getTemplateKeySuggestions(mapping)"
                        (click)="onTemplateEntityColumnSelect(mapping, suggestion)"
                      >
                        {{ suggestion }}
                      </button>
                    </div>
                  </div>

                  <div class="mapping-cell">
                    <input
                      type="text"
                      [ngModel]="mapping.nextKey"
                      (ngModelChange)="onTemplateKeyInput(mapping, $event)"
                      [class.has-change]="mapping.nextKey.trim() !== mapping.originalKey"
                    />
                  </div>

                  <div class="mapping-cell">
                    <input
                      type="text"
                      [ngModel]="mapping.displayLabel"
                      (ngModelChange)="onTemplateLabelInput(mapping, $event)"
                    />
                  </div>

                  <div class="mapping-cell mapping-cell--usage">
                    <span class="usage-count">{{ mapping.usagePaths.length }} refs</span>
                    <div class="usage-path-list">
                      <code *ngFor="let usagePath of mapping.usagePaths | slice:0:4">{{ usagePath }}</code>
                      <small *ngIf="mapping.usagePaths.length > 4">+{{ mapping.usagePaths.length - 4 }} more</small>
                    </div>
                  </div>
                </div>

                <div class="mapping-empty" *ngIf="!filteredTemplateKeyMappings.length">
                  No business keys match the current search.
                </div>
              </div>

              <div class="template-editor-feedback" *ngIf="templateValidationErrors.length || templateValidationMessage || templateSaveMessage">
                <div class="feedback-error" *ngIf="templateValidationErrors.length">
                  <strong>Validation issues</strong>
                  <ul>
                    <li *ngFor="let error of templateValidationErrors">{{ error }}</li>
                  </ul>
                </div>

                <div class="feedback-success" *ngIf="templateValidationMessage">
                  {{ templateValidationMessage }}
                </div>

                <div class="feedback-success" *ngIf="templateSaveMessage">
                  {{ templateSaveMessage }}
                </div>
              </div>
            </section>

            <section class="template-preview-panel">
              <div class="template-preview-header">
                <h5>submitPayloadTemplate JSON editor</h5>
                <small>Edit any field inside the generated <code>submitPayloadTemplate</code>. Save uses the JSON from this editor.</small>
              </div>

              <div class="template-preview-toolbar">
                <span class="usage-count">Full template editor</span>
                <button type="button" class="secondary-action compact-action" (click)="formatTemplatePreviewJson()">
                  Format JSON
                </button>
              </div>

              <textarea
                class="template-json-editor"
                [ngModel]="templatePreviewJson"
                (ngModelChange)="onTemplatePreviewJsonInput($event)"
                spellcheck="false"
              ></textarea>

              <div class="feedback-error" *ngIf="templatePreviewDraftError">
                {{ templatePreviewDraftError }}
              </div>
            </section>
          </div>

          <div class="template-editor-actions">
            <button type="button" class="secondary-action" (click)="resetTemplateEditor()">Reset</button>
            <button type="button" class="secondary-action" (click)="validateTemplateEditor()">Validate</button>
            <button type="button" class="primary-action" [disabled]="isTemplateSaving" (click)="saveTemplateEditor()">
              {{ isTemplateSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>

        <div *ngIf="uploadError" class="chat-error">
          {{ uploadError }}
        </div>
      </div>
    </div>

    <footer class="chat-footer">
      <div class="file-chip-row" *ngIf="selectedFiles.length">
        <div *ngFor="let file of selectedFiles" class="selected-file-chip">
          <img *ngIf="isImageFile(file)" [src]="getFilePreviewUrl(file)" [alt]="file.name" />
          <mat-icon *ngIf="!isImageFile(file)">insert_drive_file</mat-icon>
          <span>{{ file.name }}</span>
          <button type="button" (click)="removeFile(file)" matTooltip="Remove file">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <div class="composer">
        <label class="attach-button" matTooltip="Attach file">
          <input type="file" multiple (change)="onFileSelected($event)" hidden />
          <mat-icon>attach_file</mat-icon>
        </label>

        <textarea
          [(ngModel)]="composerText"
          rows="1"
          [disabled]="isLoading"
          placeholder="Add a note for the analyzer or upload a file..."
        ></textarea>

        <button
          type="button"
          class="send-button"
          [disabled]="!canSubmit"
          (click)="submit()"
        >
          <mat-icon>send</mat-icon>
        </button>
      </div>
    </footer>
  </div>
</section>
